import { useCallback, useMemo, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext, useInternalUserId } from '../redux/slices/appContext'
import { useNotificationsExperimentalAvailable } from '../hooks/featureChecks'
import { useWebsocketSubscription } from '../providers/WebsocketProvider'
import {
  countUnreadNotifications,
  normalizeNotificationsFromApi,
  sortNotificationsForDisplay
} from '../utils/advisorNotifications'

// Must match notifications-service notificationEvents.ts → notifications-ws-proxy push payload
const notificationPublishedTopic = 'notification/{notificationKey}/published'
const INVALIDATE_DEBOUNCE_MS = 400

const QUERY_KEYS = {
  list: (firmId, internalUserId) => ['advisor-notifications', firmId, internalUserId]
}

export function useAdvisorNotifications (options = {}) {
  const { firmId, isAdvisor } = useAppContext()
  const internalUserId = useInternalUserId()
  const notificationsExperimentalAvailable = useNotificationsExperimentalAvailable()
  const queryClient = useQueryClient()
  const queryKey = QUERY_KEYS.list(firmId, internalUserId)
  const queryEnabled = Boolean(
    isAdvisor && firmId && internalUserId && notificationsExperimentalAvailable
  )
  const invalidateDebounceRef = useRef(null)

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await postNamedQuery('notifications', 'list-user-notifications', {
        limit: options.limit ?? 100
      })
      return Array.isArray(data) ? data : []
    },
    enabled: queryEnabled,
    refetchInterval: options.refetchInterval ?? 60_000,
    refetchOnWindowFocus: true
  })

  useWebsocketSubscription((subscribe, unsubscribe) => {
    if (!queryEnabled) {
      return undefined
    }

    const scheduleInvalidate = () => {
      if (invalidateDebounceRef.current) {
        clearTimeout(invalidateDebounceRef.current)
      }
      invalidateDebounceRef.current = setTimeout(() => {
        invalidateDebounceRef.current = null
        queryClient.invalidateQueries({ queryKey })
      }, INVALIDATE_DEBOUNCE_MS)
    }

    const handle = subscribe(notificationPublishedTopic, scheduleInvalidate)
    return () => {
      unsubscribe(notificationPublishedTopic, handle)
      if (invalidateDebounceRef.current) {
        clearTimeout(invalidateDebounceRef.current)
        invalidateDebounceRef.current = null
      }
    }
  }, [queryEnabled, firmId, internalUserId, queryClient])

  const notifications = useMemo(() => {
    const normalized = normalizeNotificationsFromApi(query.data)
    return sortNotificationsForDisplay(normalized)
  }, [query.data])

  const unreadCount = useMemo(
    () => countUnreadNotifications(notifications),
    [notifications]
  )

  const markAsReadMutation = useMutation({
    mutationFn: async ({ notificationId }) => {
      const { data } = await postNamedCommand('notifications', 'mark-notification-read', {
        notificationId
      })
      return data
    },
    onMutate: async ({ notificationId }) => {
      await queryClient.cancelQueries({ queryKey })
      const snapshot = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, (old) => {
        if (!Array.isArray(old)) {
          return old
        }
        return old.map((notification) => {
          if (notification.notificationId !== notificationId) {
            return notification
          }
          return {
            ...notification,
            readDateUtc: notification.readDateUtc ?? new Date().toISOString()
          }
        })
      })
      return { snapshot }
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot !== undefined) {
        queryClient.setQueryData(queryKey, context.snapshot)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    }
  })

  const markAsRead = useCallback((notification) => {
    if (!notification?.notificationId || notification.read) {
      return
    }
    markAsReadMutation.mutate({ notificationId: notification.notificationId })
  }, [markAsReadMutation])

  return {
    notifications,
    unreadCount,
    markAsRead,
    // isLoading alone is true when disabled (status loading, fetchStatus idle)
    isLoading: query.isInitialLoading,
    isError: query.isError,
    refetch: query.refetch
  }
}
